/**
 * Sale Order API Service
 */

import { apiClient } from '../client';
import type {
  ApiResponse,
  PaginatedData,
  SaleOrder,
  SaleOrderDetail,
  CommissionData,
  DepositInvoiceResult,
  EinvoicePdfResult,
} from '../../types';

const API_PREFIX = '/api/saiens/sale';

interface OrderListParams {
  offset?: number;
  limit?: number;
  state?: string;
  partner_id?: number;
  user_id?: number;
  search?: string;
  my_orders?: boolean;
  created_by_me?: boolean;
  date_from?: string;
  date_to?: string;
  pay_date_from?: string;
  pay_date_to?: string;
}

/**
 * Get sale order list
 */
export async function getSaleOrders(
  params: OrderListParams = {}
): Promise<ApiResponse<PaginatedData<SaleOrder>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<SaleOrder>>>(
    `${API_PREFIX}/orders`,
    { params }
  );
  return response.data;
}

/**
 * Get sale order detail
 */
export async function getSaleOrder(orderId: number): Promise<ApiResponse<SaleOrderDetail>> {
  const response = await apiClient.get<ApiResponse<SaleOrderDetail>>(
    `${API_PREFIX}/orders/${orderId}`
  );
  return response.data;
}

/**
 * Get order commission data
 */
export async function getOrderCommission(orderId: number): Promise<ApiResponse<CommissionData>> {
  const response = await apiClient.get<ApiResponse<CommissionData>>(
    `${API_PREFIX}/orders/${orderId}/commission`
  );
  return response.data;
}

/**
 * Confirm sale order
 */
export async function confirmOrder(orderId: number): Promise<ApiResponse<SaleOrder>> {
  const response = await apiClient.post<ApiResponse<SaleOrder>>(
    `${API_PREFIX}/orders/${orderId}/confirm`
  );
  return response.data;
}

/**
 * Get quote link for customer signing
 */
export async function getQuoteLink(
  orderId: number
): Promise<ApiResponse<{ quote_url: string; order_name: string }>> {
  const response = await apiClient.post<
    ApiResponse<{ quote_url: string; order_name: string }>
  >(`${API_PREFIX}/orders/${orderId}/send-quote-link`);
  return response.data;
}

/**
 * List signed orders without any deposit invoice
 */
export async function getSignedWithoutDeposit(
  params: { offset?: number; limit?: number } = {}
): Promise<ApiResponse<PaginatedData<SaleOrder>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<SaleOrder>>>(
    `${API_PREFIX}/signed-without-deposit`,
    { params }
  );
  return response.data;
}

/**
 * Create deposit invoice (draft) and auto-issue e-invoice
 */
export async function createDepositInvoice(
  orderId: number,
  percentage = 50,
  itemName = '石材工程預收款'
): Promise<ApiResponse<DepositInvoiceResult>> {
  const response = await apiClient.post<ApiResponse<DepositInvoiceResult>>(
    `${API_PREFIX}/orders/${orderId}/create-deposit-invoice`,
    { percentage, item_name: itemName }
  );
  return response.data;
}

/**
 * Get e-invoice PDF URL on demand from Amego
 */
export async function getEinvoicePdf(
  orderId: number
): Promise<ApiResponse<EinvoicePdfResult>> {
  const response = await apiClient.get<ApiResponse<EinvoicePdfResult>>(
    `${API_PREFIX}/orders/${orderId}/einvoice-pdf`
  );
  return response.data;
}

/**
 * Generate deposit package PDF link (order + invoice + e-invoice merged PDF)
 */
export async function getDepositPackageLink(
  orderId: number
): Promise<ApiResponse<{ pdf_url: string; order_name: string }>> {
  const response = await apiClient.post<
    ApiResponse<{ pdf_url: string; order_name: string }>
  >(`${API_PREFIX}/orders/${orderId}/deposit-package-link`);
  return response.data;
}
